// fields/MultiSearchSelectField.js
// ============================================
// API-based multi-select search field with debouncing
// ============================================
import React, { useState, useEffect, useRef, useCallback } from 'react';

export const MultiSearchSelectField = ({ 
  field, 
  register, 
  errors, 
  theme, 
  setValue, 
  watch,
  searchEndpoint, // API endpoint for search
  searchParams = {}, // Additional params to send with search
  minSearchLength = 2, // Minimum characters before searching
  debounceMs = 300, // Debounce delay
  placeholder = "Type to search and select multiple...",
  displayFormat = "full",
  customDisplay = null,
  allowClear = true,
  maxDropdownHeight = "300px",
  loadingText = "Searching...",
  noResultsText = "No results found",
  searchErrorText = "Error loading results",
  transformResponse = null, // Function to transform API response
  getOptionValue = (option) => option.id, // Function to get option value
  getOptionLabel = (option) => option.displayName, // Function to get option label
  getOptionInfo = (option) => option.info, // Function to get option info
  maxTags = null, // Maximum number of selections allowed
  tagVariant = "default", // "default", "compact", "minimal"
  showSelectedCount = true, // Show count when collapsed
  collapseTags = true, // Collapse tags after certain number
  collapseAfter = 3, // Number of tags to show before collapsing
  hideDropdownOnSelect = false, // Keep dropdown open after selection
  selectAllOption = true, // Show "Select All" option for current results
  loadInitialValues = true, // Load full data for initial IDs
  batchLoadEndpoint = null, // Optional endpoint for batch loading initial values
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isTagsExpanded, setIsTagsExpanded] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceTimer = useRef(null);
  const abortController = useRef(null);
  
  // Store props in refs to avoid recreating performSearch
  const searchEndpointRef = useRef(searchEndpoint);
  const searchParamsRef = useRef(searchParams);
  const minSearchLengthRef = useRef(minSearchLength);
  const transformResponseRef = useRef(transformResponse);
  const batchLoadEndpointRef = useRef(batchLoadEndpoint);
  
  // Update refs when props change
  useEffect(() => {
    searchEndpointRef.current = searchEndpoint;
    searchParamsRef.current = searchParams;
    minSearchLengthRef.current = minSearchLength;
    transformResponseRef.current = transformResponse;
    batchLoadEndpointRef.current = batchLoadEndpoint;
  }, [searchEndpoint, searchParams, minSearchLength, transformResponse, batchLoadEndpoint]);
  
  const fieldTheme = theme.fields?.multiSearchSelect || theme.fields?.searchSelect || theme.fields?.select || {};
  const dropdownTheme = theme.dropdown || {};
  const tagTheme = theme.tags || {};
  
  const currentValue = watch(field.id) || [];
  
  // Load initial values if provided
  useEffect(() => {
    if (loadInitialValues && Array.isArray(currentValue) && currentValue.length > 0 && selectedItems.length === 0) {
      loadInitialValuesData(currentValue);
    }
  }, [currentValue, loadInitialValues]);
  
  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);
  
  // Load initial values from API
  const loadInitialValuesData = async (ids) => {
    setIsLoadingInitial(true);
    try {
      let items = [];
      
      if (batchLoadEndpointRef.current) {
        // Batch load if endpoint provided
        const response = await fetch(batchLoadEndpointRef.current, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ids }),
        });
        
        if (response.ok) {
          const data = await response.json();
          items = transformResponseRef.current ? transformResponseRef.current(data) : data;
        }
      } else {
        // Load one by one
        const promises = ids.map(async (id) => {
          try {
            const response = await fetch(`${searchEndpointRef.current}/${id}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            });
            
            if (response.ok) {
              const data = await response.json();
              return transformResponseRef.current ? transformResponseRef.current([data])[0] : data;
            }
          } catch (error) {
            console.error(`Failed to load item ${id}:`, error);
            return null;
          }
        });
        
        const results = await Promise.all(promises);
        items = results.filter(item => item !== null);
      }
      
      setSelectedItems(items);
    } catch (error) {
      console.error('Failed to load initial values:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  };
  
  // Stable search function
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < minSearchLengthRef.current) {
      setSearchResults([]);
      return;
    }
    
    // Cancel previous request
    if (abortController.current) {
      abortController.current.abort();
    }
    
    // Create new abort controller
    abortController.current = new AbortController();
    
    setIsLoading(true);
    setSearchError(null);
    
    try {
      const params = new URLSearchParams({
        q: query,
        ...searchParamsRef.current,
      });
      
      const response = await fetch(`${searchEndpointRef.current}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortController.current.signal,
      });
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform response if transformer provided
      const results = transformResponseRef.current ? transformResponseRef.current(data) : data;
      
      // Filter out already selected items from search results
      const filteredResults = Array.isArray(results) 
        ? results.filter(item => !selectedItems.some(selected => getOptionValue(selected) === getOptionValue(item)))
        : [];
      
      setSearchResults(filteredResults);
      setHighlightedIndex(0);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        setSearchError(error.message);
        setSearchResults([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedItems, getOptionValue]);
  
  // Handle search input changes with debouncing
  useEffect(() => {
    if (!searchTerm || searchTerm.length < minSearchLength) {
      setSearchResults([]);
      return;
    }
    
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    debounceTimer.current = setTimeout(() => {
      performSearch(searchTerm);
    }, debounceMs);
    
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm, minSearchLength, debounceMs, performSearch]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsTagsExpanded(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Handle backspace to remove last selected item
    if (e.key === 'Backspace' && searchTerm === '' && selectedItems.length > 0 && !field.disabled) {
      e.preventDefault();
      const newItems = selectedItems.slice(0, -1);
      handleSelectionChange(newItems);
      return;
    }
    
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }
    
    if (!isOpen) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const maxIndex = selectAllOption && searchResults.length > 1 ? searchResults.length : searchResults.length - 1;
          return prev < maxIndex ? prev + 1 : prev;
        });
        scrollToHighlighted();
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        scrollToHighlighted();
        break;
        
      case 'Enter':
        e.preventDefault();
        if (selectAllOption && searchResults.length > 1 && highlightedIndex === 0) {
          handleSelectAll();
        } else {
          const adjustedIndex = selectAllOption && searchResults.length > 1 ? highlightedIndex - 1 : highlightedIndex;
          if (searchResults[adjustedIndex]) {
            handleToggleSelect(searchResults[adjustedIndex]);
          }
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
        
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };
  
  const scrollToHighlighted = () => {
    setTimeout(() => {
      const highlighted = dropdownRef.current?.querySelector('[data-highlighted="true"]');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  };
  
  const handleToggleSelect = (option) => {
    const isSelected = selectedItems.some(item => getOptionValue(item) === getOptionValue(option));
    let newItems;
    
    if (isSelected) {
      newItems = selectedItems.filter(item => getOptionValue(item) !== getOptionValue(option));
    } else {
      if (maxTags && selectedItems.length >= maxTags) {
        return; // Don't add if max reached
      }
      newItems = [...selectedItems, option];
    }
    
    handleSelectionChange(newItems);
    
    if (!hideDropdownOnSelect) {
      // Keep dropdown open and maintain search
      performSearch(searchTerm);
    } else {
      setIsOpen(false);
      setSearchTerm('');
    }
  };
  
  const handleSelectionChange = (newItems) => {
    setSelectedItems(newItems);
    const ids = newItems.map(item => getOptionValue(item));
    setValue(field.id, ids, { shouldValidate: true });
  };
  
  const handleSelectAll = () => {
    const availableItems = searchResults.slice(0, maxTags ? Math.min(maxTags - selectedItems.length, searchResults.length) : searchResults.length);
    const newItems = [...selectedItems, ...availableItems];
    handleSelectionChange(newItems);
    
    if (!hideDropdownOnSelect) {
      performSearch(searchTerm);
    }
  };
  
  const handleRemoveItem = (itemToRemove, e) => {
    e.stopPropagation();
    const newItems = selectedItems.filter(item => getOptionValue(item) !== getOptionValue(itemToRemove));
    handleSelectionChange(newItems);
  };
  
  const handleClearAll = (e) => {
    e.stopPropagation();
    handleSelectionChange([]);
    setSearchTerm('');
    setSearchResults([]);
    setIsTagsExpanded(false);
  };
  
  const handleInputClick = () => {
    setIsOpen(true);
    inputRef.current?.focus();
  };
  
  const getDisplayText = (item) => {
    if (!item) return '';
    
    if (customDisplay) {
      return customDisplay(item);
    }
    
    const label = getOptionLabel(item);
    const info = getOptionInfo(item);
    
    switch (displayFormat) {
      case 'nameOnly':
        return label;
      case 'full':
        return info ? `${label} - ${info}` : label;
      default:
        return label;
    }
  };
  
  const getInputClassName = () => {
    const classes = [
      fieldTheme.base || theme.input,
      'cursor-pointer'
    ];
    
    if (isFocused) classes.push(fieldTheme.focus);
    if (errors[field.id]) classes.push(fieldTheme.error || theme.inputError);
    if (field.disabled) classes.push(fieldTheme.disabled);
    if (selectedItems.length > 0) classes.push(fieldTheme.filled);
    if (isLoading || isLoadingInitial) classes.push('animate-pulse');
    
    return classes.join(' ');
  };
  
  const getTagClassName = () => {
    const baseClasses = tagTheme.base || 'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
    
    switch (tagVariant) {
      case 'compact':
        return tagTheme.compact || 'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
      case 'minimal':
        return tagTheme.minimal || 'inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
      default:
        return baseClasses;
    }
  };
  
  // Determine which tags to show
  const visibleTags = collapseTags && !isTagsExpanded && selectedItems.length > collapseAfter
    ? selectedItems.slice(0, collapseAfter)
    : selectedItems;
  const hiddenCount = selectedItems.length - visibleTags.length;
  
  // Check if all current search results are selected
  const allResultsSelected = searchResults.length > 0 && 
    searchResults.every(result => selectedItems.some(item => getOptionValue(item) === getOptionValue(result)));
  
  return (
    <div className="relative" ref={containerRef}>
      {/* Selected Tags Display */}
      {selectedItems.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5 items-center">
          {visibleTags.map(item => (
            <span key={getOptionValue(item)} className={getTagClassName()}>
              <span className="truncate max-w-[200px]">
                {tagVariant === 'minimal' ? getOptionLabel(item) : getDisplayText(item)}
              </span>
              {!field.disabled && (
                <button
                  type="button"
                  onClick={(e) => handleRemoveItem(item, e)}
                  className="ml-1 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </span>
          ))}
          
          {/* Show more/less button */}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setIsTagsExpanded(!isTagsExpanded)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              +{hiddenCount} more
            </button>
          )}
          
          {/* Clear all button */}
          {allowClear && !field.disabled && selectedItems.length > 1 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-sm text-red-600 dark:text-red-400 hover:underline ml-2"
            >
              Clear all
            </button>
          )}
        </div>
      )}
      
      <div className="relative">
        {/* Search Input */}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onClick={handleInputClick}
          onFocus={() => {
            setIsFocused(true);
            setIsOpen(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={selectedItems.length > 0 
            ? `${selectedItems.length} selected${maxTags ? ` (max ${maxTags})` : ''}` 
            : placeholder}
          className={getInputClassName()}
          disabled={field.disabled || isLoadingInitial}
          autoComplete="off"
        />
        
        {/* Icons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {/* Loading spinner */}
          {(isLoading || isLoadingInitial) && (
            <svg className="animate-spin h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          
          {/* Clear button */}
          {!isLoading && !isLoadingInitial && allowClear && selectedItems.length > 0 && !field.disabled && (
            <button
              type="button"
              onClick={handleClearAll}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          
          {/* Selected count badge */}
          {showSelectedCount && selectedItems.length > 0 && !isLoading && !isLoadingInitial && (
            <span className="px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full">
              {selectedItems.length}
            </span>
          )}
          
          {/* Search/Dropdown icon */}
          {!isLoading && !isLoadingInitial && (
            <svg 
              className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              {searchResults.length > 0 || selectedItems.length > 0 ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              )}
            </svg>
          )}
        </div>
      </div>
      
      {/* Dropdown */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className={dropdownTheme.container || 'absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg overflow-auto'}
          style={{ maxHeight: maxDropdownHeight }}
        >
          {/* Loading state */}
          {isLoading && (
            <div className={dropdownTheme.loadingContainer || 'px-3 py-4 text-center text-gray-500 dark:text-gray-400'}>
              <svg 
                className={dropdownTheme.loadingSpinner || 'animate-spin h-5 w-5 mx-auto mb-2'} 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {loadingText}
            </div>
          )}
          
          {/* Error state */}
          {!isLoading && searchError && (
            <div className={dropdownTheme.errorText || 'px-3 py-2 text-red-500 dark:text-red-400 text-center'}>
              {searchErrorText}
            </div>
          )}
          
          {/* Results */}
          {!isLoading && !searchError && searchResults.length > 0 && (
            <>
              {/* Select All Option */}
              {selectAllOption && searchResults.length > 1 && (
                <>
                  <div
                    data-highlighted={highlightedIndex === 0}
                    onClick={handleSelectAll}
                    onMouseEnter={() => setHighlightedIndex(0)}
                    className={
                      highlightedIndex === 0
                        ? (dropdownTheme.itemHighlighted || 'px-3 py-2 cursor-pointer transition-colors bg-blue-50 dark:bg-blue-900/30')
                        : (dropdownTheme.item || 'px-3 py-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700')
                    }
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={allResultsSelected}
                        onChange={() => {}}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="font-medium">
                        {allResultsSelected ? 'Deselect All Results' : `Select All Results (${Math.min(searchResults.length, maxTags ? maxTags - selectedItems.length : searchResults.length)})`}
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                </>
              )}
              
              {/* Search Results */}
              {searchResults.map((option, index) => {
                const actualIndex = selectAllOption && searchResults.length > 1 ? index + 1 : index;
                const isSelected = selectedItems.some(item => getOptionValue(item) === getOptionValue(option));
                const isDisabled = maxTags && selectedItems.length >= maxTags && !isSelected;
                
                return (
                  <div
                    key={getOptionValue(option)}
                    data-highlighted={actualIndex === highlightedIndex}
                    onClick={() => !isDisabled && handleToggleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(actualIndex)}
                    className={`${
                      actualIndex === highlightedIndex
                        ? (dropdownTheme.itemHighlighted || 'px-3 py-2 cursor-pointer transition-colors bg-blue-50 dark:bg-blue-900/30')
                        : (dropdownTheme.item || 'px-3 py-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700')
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        disabled={isDisabled}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className={dropdownTheme.itemLabel || 'font-medium text-gray-900 dark:text-gray-100'}>
                          {getOptionLabel(option)}
                        </div>
                        {getOptionInfo(option) && (
                          <div className={dropdownTheme.itemInfo || 'text-sm text-gray-500 dark:text-gray-400 mt-0.5'}>
                            {getOptionInfo(option)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
          
          {/* No results */}
          {!isLoading && !searchError && searchResults.length === 0 && searchTerm.length >= minSearchLength && (
            <div className={dropdownTheme.noResultsText || 'px-3 py-2 text-gray-500 dark:text-gray-400 text-center'}>
              {noResultsText}
            </div>
          )}
          
          {/* Prompt to search */}
          {!isLoading && searchTerm.length < minSearchLength && searchTerm.length > 0 && (
            <div className={dropdownTheme.promptText || 'px-3 py-2 text-gray-500 dark:text-gray-400 text-center'}>
              Type at least {minSearchLength} characters to search
            </div>
          )}
          
          {/* Max selection message */}
          {maxTags && selectedItems.length >= maxTags && (
            <div className="px-3 py-2 text-sm text-orange-600 dark:text-orange-400 border-t border-gray-200 dark:border-gray-700">
              Maximum {maxTags} items can be selected
            </div>
          )}
        </div>
      )}
      
      {/* Hidden input for form value */}
      <input
        type="hidden"
        {...register(field.id, field.validation)}
        value={selectedItems.map(item => getOptionValue(item)).join(',')}
      />
    </div>
  );
};